// route-level code splitting
import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES, SPLIT_NAMES, SPLIT_TREATMENTS } from '@/constants';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { toNonVueRoute } from './utils';

const InventoryView = () => import('@/pages/Inventory/index.vue');
const ReconcileInventory = () => import('@/pages/Inventory/Reconcile/ReconcileInventory.vue');
const ListingView = () => import('@/pages/Inventory/Listings/ListingsOverview.vue');
const ListingAllocationsOverview = () => import('@/pages/Inventory/Listings/ListingAllocationsOverview.vue');
const InventoryOverview = () => import('@/pages/Inventory/InventoryOverview/index.vue');
const ProductList = () => import('@/pages/Inventory/Products/index.vue');
const ProductTemplatesOverview = () => import('@/pages/Inventory/ProductTemplates/ProductTemplatesOverview.vue');
const ProductLines = () => import('@/pages/Inventory/ProductLines/ProductLines.vue');
const ProductAdd = () => import('@/pages/Inventory/Products/AddProduct.vue');
const ProductEdit = () => import('@/pages/Inventory/Products/EditProduct.vue');
const ProductClone = () => import('@/pages/Inventory/Products/CloneProduct.vue');
const ProductAddTemplate = () => import('@/pages/Inventory/Products/AddProductTemplate.vue');

export const InventoryTabRoutes = () =>
  Object.freeze([
    {
      to: { name: ROUTE_NAMES.PRODUCTS },
      label: i18n.t('inventory.tabs.active_products'),
      visible: ({ isSplitEnabled }) => {
        return !isSplitEnabled(SPLIT_NAMES.PRODUCT_LIST_PAGE);
      },
    },
    {
      to: { name: ROUTE_NAMES.PRODUCTS_NEW },
      label: i18n.t('inventory.tabs.active_products'),
      visible: ({ isSplitEnabled }) => {
        return isSplitEnabled(SPLIT_NAMES.PRODUCT_LIST_PAGE);
      },
    },
    {
      to: { name: ROUTE_NAMES.INVENTORY_OVERVIEW },
      label: i18n.t('common.inventory'),
      visible: ({ isSplitEnabled }) =>
        isSplitEnabled(SPLIT_NAMES.INVENTORY_FACILITY) || isSplitEnabled(SPLIT_NAMES.INVENTORY_BATCH),
    },
    {
      to: { name: ROUTE_NAMES.PRODUCTS_ARCHIVED },
      label: i18n.t('inventory.tabs.archived'),
    },
    {
      to: { name: ROUTE_NAMES.LISTINGS },
      label: i18n.t('inventory.tabs.listings'),
      visible: ({ isSplitEnabled }) => {
        return !isSplitEnabled(SPLIT_NAMES.LISTINGS_ALLOCATIONS);
      },
    },
    {
      to: { name: ROUTE_NAMES.LISTINGS_NEW },
      label: i18n.t('inventory.tabs.listings'),
      visible: ({ isSplitEnabled }) => {
        return isSplitEnabled(SPLIT_NAMES.LISTINGS_ALLOCATIONS);
      },
    },
    {
      to: { name: ROUTE_NAMES.BATCH_MANAGEMENT },
      label: i18n.t('inventory.tabs.batch_management'),
    },
    // The custom menus are linked in both the inventory and pricing tabs, though we should really remove this one
    {
      to: { name: ROUTE_NAMES.PRICING_CUSTOM_MENUS },
      label: i18n.t('inventory.tabs.custom_menus'),
    },
    {
      to: { name: ROUTE_NAMES.PROMO_CODES },
      label: i18n.t('inventory.tabs.promotions'),
    },
    {
      to: { name: ROUTE_NAMES.PRODUCT_LINES },
      label: i18n.t('inventory.tabs.product_lines'),
    },
    {
      to: { name: ROUTE_NAMES.PRODUCT_TEMPLATES },
      label: i18n.t('inventory.tabs.templates'),
    },
    {
      to: { name: ROUTE_NAMES.PRODUCTS_ADD_BULK },
      label: i18n.t('inventory.tabs.bulkAddEdit'),
    },
    {
      to: { name: ROUTE_NAMES.STRAINS },
      label: i18n.t('inventory.tabs.strain'),
    },
    {
      to: { name: ROUTE_NAMES.PACKAGING_LIST },
      label: i18n.t('inventory.tabs.product_packaging'),
    },
    {
      to: { name: ROUTE_NAMES.QUESTIONS_ANSWERS },
      label: i18n.t('inventory.tabs.faqs'),
    },
  ]);

const inventoryRoutes: RouteRecordRaw[] = [
  // Legacy routes
  toNonVueRoute({
    path: 'inventory/products/',
    name: ROUTE_NAMES.PRODUCTS,
    meta: {
      permissions: [
        {
          rules: ['canManageInventory'],
        },
        {
          rules: [({ isSplitEnabled }) => !isSplitEnabled('inventory_pod__new_product_list_page')],
          redirect: { name: ROUTE_NAMES.PRODUCTS_NEW },
        },
      ],
    },
  }),
  toNonVueRoute({ path: 'inventory/products/:productId/review/', name: ROUTE_NAMES.PRODUCT_REVIEW }),
  toNonVueRoute({ path: 'inventory/strains/', name: ROUTE_NAMES.STRAINS }),
  toNonVueRoute({ path: 'inventory/manage-questions-answers/', name: ROUTE_NAMES.QUESTIONS_ANSWERS }),
  toNonVueRoute({ path: 'inventory/products/archived/', name: ROUTE_NAMES.PRODUCTS_ARCHIVED }),
  toNonVueRoute({ path: 'inventory/product-packaging/', name: ROUTE_NAMES.PACKAGING_LIST }),
  toNonVueRoute({ path: 'inventory/batch-management/', name: ROUTE_NAMES.BATCH_MANAGEMENT }),
  toNonVueRoute({ path: 'inventory/products/add/bulk/', name: ROUTE_NAMES.PRODUCTS_ADD_BULK }),
  toNonVueRoute({ path: 'inventory/products/add/', name: ROUTE_NAMES.PRODUCTS_ADD }),
  toNonVueRoute({ path: 'inventory/products/add_from_template/:template/', name: ROUTE_NAMES.PRODUCTS_ADD_TEMPLATE }),
  toNonVueRoute({ path: 'inventory/products/:productId/log/', name: ROUTE_NAMES.PRODUCT_AUDIT_LOG }),
  toNonVueRoute({ path: 'inventory/products/:productId/edit/', name: ROUTE_NAMES.PRODUCT_EDIT }),
  toNonVueRoute({ path: 'inventory/products/:productId/clone/', name: ROUTE_NAMES.PRODUCT_CLONE }),

  // Vue routes
  {
    component: InventoryView,
    path: 'inventory/',
    beforeEnter: async () => {
      await Promise.all([loadTranslations('inventory'), loadTranslations('crm'), loadTranslations('advertising')]);
    },
    meta: {
      tabs: InventoryTabRoutes,
    },
    children: [
      {
        path: 'reconcile/',
        name: ROUTE_NAMES.RECONCILE_INVENTORY,
        component: ReconcileInventory,
        meta: {
          title: () => i18n.t('inventory.reconcileInventory.title'),
          permissions: [
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'listings/',
        name: ROUTE_NAMES.LISTINGS,
        component: ListingView,
        meta: {
          title: () => i18n.t('inventory.listings.title'),
          description: () => i18n.t('inventory.listings.description'),
          permissions: [
            {
              rules: [({ isSplitEnabled }) => !isSplitEnabled('inventory_pod__listings_allocations')],
              redirect: (to) => ({ name: ROUTE_NAMES.LISTINGS_NEW, params: { company: to.params.company } }),
            },
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'listings-new/',
        name: ROUTE_NAMES.LISTINGS_NEW,
        component: ListingAllocationsOverview,
        meta: {
          title: () => i18n.t('inventory.listings.title'),
          description: () => i18n.t('inventory.listings.description'),
          permissions: [
            {
              rules: ['inventory_pod__listings_allocations'],
              redirect: (to) => ({ name: ROUTE_NAMES.LISTINGS, params: { company: to.params.company } }),
            },
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'products-new',
        name: ROUTE_NAMES.PRODUCTS_NEW,
        component: ProductList,
        meta: {
          title: () => i18n.t('common.products'),
          permissions: [
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
            {
              rules: ['inventory_pod__new_product_list_page'],
              redirect: { name: ROUTE_NAMES.PRODUCTS },
            },
          ],
        },
      },
      {
        path: 'products-v2/',
        meta: {
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE, [
              SPLIT_TREATMENTS.ON,
              SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_LEGACY,
              SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_MVP,
            ]);
          },
          permissions: [
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
                // Split treatment for the new product add/edit page
                {
                  [SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE]: [
                    SPLIT_TREATMENTS.ON,
                    SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_MVP,
                    SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_LEGACY,
                  ],
                },
                // Check if user has the new product add/edit page enabled
                () => {
                  const { getIsSplitEnabled } = useUserStore();
                  // if split is "on", it means user will always see the new product add/edit page
                  if (getIsSplitEnabled(SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE)) {
                    return true;
                  }

                  // Checks if the split treatment defaults to MVP experience
                  const isSplitMVPDefaultEnabled = getIsSplitEnabled(SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE, [
                    SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_MVP,
                  ]);

                  // Checks whether the user opted in or out of using the new product add/edit page.
                  // For example, the user might be assigned to the MVP treatment by default, but chose to use the legacy version instead.
                  const { isProductAddEditMVP } = useUserStore().getUserSettingsByUser('inventory') || {};

                  return (
                    (isSplitMVPDefaultEnabled && isProductAddEditMVP === undefined) || Boolean(isProductAddEditMVP)
                  );
                },
              ],
              redirect(to) {
                let name;

                switch (to.name) {
                  case ROUTE_NAMES.PRODUCTS_V2_EDIT:
                    name = ROUTE_NAMES.PRODUCT_EDIT;
                    break;
                  case ROUTE_NAMES.PRODUCTS_V2_ADD_TEMPLATE:
                    name = ROUTE_NAMES.PRODUCTS_ADD_TEMPLATE;
                    break;
                  case ROUTE_NAMES.PRODUCTS_V2_CLONE:
                    name = ROUTE_NAMES.PRODUCT_CLONE;
                    break;
                  default:
                    name = ROUTE_NAMES.PRODUCTS_ADD;
                }

                return { name, params: to.params };
              },
            },
          ],
        },
        children: [
          {
            path: '',
            name: ROUTE_NAMES.PRODUCTS_V2,
            redirect: (to) => ({ name: ROUTE_NAMES.PRODUCTS_NEW, params: { company: to.params.company } }),
          },
          {
            path: 'add/',
            name: ROUTE_NAMES.PRODUCTS_V2_ADD,
            component: ProductAdd,
            meta: {
              title: () => i18n.t('inventory.productAddEdit.add.title'),
              description: () => i18n.t('inventory.productAddEdit.add.description'),
            },
          },
          {
            path: ':productId(\\d+)/edit/',
            name: ROUTE_NAMES.PRODUCTS_V2_EDIT,
            component: ProductEdit,
            meta: {
              title: () => i18n.t('inventory.productAddEdit.edit.title'),
              description: () => i18n.t('inventory.productAddEdit.edit.description'),
            },
          },
          {
            path: ':productId(\\d+)/clone/',
            name: ROUTE_NAMES.PRODUCTS_V2_CLONE,
            component: ProductClone,
            meta: {
              title: () => i18n.t('inventory.productAddEdit.clone.title'),
              description: () => i18n.t('inventory.productAddEdit.clone.description'),
            },
          },
          {
            path: 'add_from_template/:template(\\d+)',
            name: ROUTE_NAMES.PRODUCTS_V2_ADD_TEMPLATE,
            component: ProductAddTemplate,
            meta: {
              title: () => i18n.t('inventory.productAddEdit.add.title'),
              description: () => i18n.t('inventory.productAddEdit.add.description'),
            },
          },
        ],
      },
      {
        path: 'product-templates',
        name: ROUTE_NAMES.PRODUCT_TEMPLATES,
        component: ProductTemplatesOverview,
        meta: {
          title: () => i18n.t('inventory.productTemplates.title'),
          description: () => i18n.t('inventory.productTemplates.description'),
          permissions: [
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'product-lines/',
        name: ROUTE_NAMES.PRODUCT_LINES,
        component: ProductLines,
        meta: {
          title: () => i18n.t('inventory.productLines.title'),
          description: () => i18n.t('inventory.productLines.description'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_INVENTORY_PRODUCT_LINE_PAGE);
          },
          permissions: [
            {
              rules: [
                'canManageInventory',
                'vue_inventory_product_line_page',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: '',
        name: ROUTE_NAMES.INVENTORY_OVERVIEW,
        component: InventoryOverview,
        meta: {
          title: () => i18n.t('common.inventory'),
          description: () => i18n.t('inventory.inventoryOverview.description'),
          permissions: [
            {
              rules: [
                'canManageInventory',
                ({ userActiveCompany, isSplitEnabled }) =>
                  Boolean(userActiveCompany?.seller && userActiveCompany.staffId) &&
                  (isSplitEnabled('inventory_pod__inventory_warehouse_support') ||
                    isSplitEnabled('inventory_pod__inventory_batch_support')),
              ],
            },
          ],
        },
      },
    ],
  },
];

export default inventoryRoutes;
